import type * as api from '@gitbook/api-client';
import { type Document, serialize } from '@gitbook/slate';

import { apiClient } from '../../state';

/**
 * Returns all possible actions for the given Comment or CommentReply.
 */
export function useCommentActions(props: {
    comment: api.Comment;
    commentReply?: api.CommentReply;
}): {
    onReply?: (body: Document) => Promise<void>;
    onResolve?: () => Promise<void>;
    onUnresolve?: () => Promise<void>;
    onDelete: () => Promise<void>;
    onEdit: (body: Document) => Promise<void>;
    onReact: (emoji: string) => Promise<void>;
    onUnreact: (emoji: string) => Promise<void>;
} {
    const { comment, commentReply } = props;
    const commentId = comment.id;
    const commentReplyId = commentReply?.id;
    const spaceId = comment.target.space;
    const changeRequestId = comment.target.changeRequest;
    const canReply = !commentReplyId;
    const isResolved = comment.status === 'resolved';
    const canResolve = !commentReplyId && !isResolved;
    const canUnresolve = !commentReplyId && isResolved;

    /**
     * Updates a comment or commentReply. Used for onEdit, onResolve, onUnresolve, onReact and onUnreact.
     */
    async function updateCommentAndWaitForMutations(
        data: api.UpdateCommentSchema | api.UpdateCommentReplySchema
    ) {
        changeRequestId
            ? commentReplyId
                ? await apiClient.changeRequestComments.updateCommentReplyInChangeRequest(
                      spaceId,
                      changeRequestId,
                      commentId,
                      commentReplyId,
                      data
                  )
                : await apiClient.changeRequestComments.updateCommentInChangeRequest(
                      spaceId,
                      changeRequestId,
                      commentId,
                      data
                  )
            : commentReplyId
              ? await apiClient.spaceComments.updateCommentReplyInSpace(
                    spaceId,
                    commentId,
                    commentReplyId,
                    data
                )
              : await apiClient.spaceComments.updateCommentInSpace(spaceId, commentId, data);
    }

    /**
     * Posts a reply to the given comment and waits for mutations
     */
    async function onReply(body: Document) {
        const data = {
            body: {
                document: serialize(body),
            },
        };
        changeRequestId
            ? await apiClient.changeRequestComments.postCommentReplyInChangeRequest(
                  spaceId,
                  changeRequestId,
                  commentId,
                  data
              )
            : await apiClient.spaceComments.postCommentReplyInSpace(spaceId, commentId, data);
    }

    /**
     * Resolves the given comment or comment reply, if not already unresolved and waits for mutations
     */
    async function onResolve() {
        await updateCommentAndWaitForMutations({ resolved: true });
    }

    /**
     * Unresolves the given comment or comment reply, if resolved and waits for mutations
     */
    async function onUnresolve() {
        await updateCommentAndWaitForMutations({ resolved: false });
    }

    /**
     * Deletes the given comment or comment reply and waits for mutations
     */
    async function onDelete() {
        changeRequestId
            ? commentReplyId
                ? await apiClient.changeRequestComments.deleteCommentReplyInChangeRequest(
                      spaceId,
                      changeRequestId,
                      commentId,
                      commentReplyId
                  )
                : await apiClient.changeRequestComments.deleteCommentInChangeRequest(
                      spaceId,
                      changeRequestId,
                      commentId
                  )
            : commentReplyId
              ? await apiClient.spaceComments.deleteCommentReplyInSpace(
                    spaceId,
                    commentId,
                    commentReplyId
                )
              : await apiClient.spaceComments.deleteCommentInSpace(spaceId, commentId);
    }

    /**
     * Updates the body of the given comment or comment reply and waits for mutations
     */
    async function onEdit(body: Document) {
        await updateCommentAndWaitForMutations({
            body: {
                document: serialize(body),
            },
        });
    }
    /**
     * Adds a reaction to the given comment or comment reply and waits for mutations
     */
    async function onReact(emoji: string) {
        await updateCommentAndWaitForMutations({ addedReactions: [emoji] });
    }

    /**
     * Removes a reaction from the given comment or comment reply and waits for mutations
     */
    async function onUnreact(emoji: string) {
        await updateCommentAndWaitForMutations({ removedReactions: [emoji] });
    }

    return {
        onReply: !canReply ? undefined : onReply,
        onResolve: !canResolve ? undefined : onResolve,
        onUnresolve: !canUnresolve ? undefined : onUnresolve,
        onDelete,
        onEdit,
        onReact,
        onUnreact,
    };
}
